import React, { useState } from 'react';
import { certificateAPI } from '../services/api';
import toast from 'react-hot-toast';

const StudentAccess = () => {
  const [apiCode, setApiCode] = useState('');
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!apiCode.trim()) {
      toast.error('Please enter your API code');
      return;
    }

    setLoading(true);
    try {
      const response = await certificateAPI.getCertificateByApiCode(apiCode.trim());
      setCertificate(response.data.data.certificate);
      toast.success('Certificate found!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Certificate not found');
      setCertificate(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="student-access-page">
      <div className="container" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div className="mb-8 text-center">
          <h1 style={{ fontSize: '2rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            Access Your Certificate
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Enter your API code to view and verify your certificate
          </p>
        </div>

        <div className="card mb-8">
          <div className="card-body">
            <form onSubmit={handleSubmit} className="form">
              <div className="form-group">
                <label htmlFor="apiCode" className="form-label">
                  Certificate API Code
                </label>
                <input
                  type="text"
                  id="apiCode"
                  value={apiCode}
                  onChange={(e) => setApiCode(e.target.value)}
                  className="form-input"
                  placeholder="CERT-XXXXXXXX-XXXXXXXX"
                  disabled={loading}
                />
                <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                  Enter the API code provided by your institution
                </small>
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    Searching...
                  </>
                ) : (
                  'Access Certificate'
                )}
              </button>
            </form>
          </div>
        </div>

        {certificate && (
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Complete Academic Record</h2>
            </div>
            <div className="card-body">
              <div className="mb-6">
                <h4 style={{ fontWeight: '600', marginBottom: '1rem', color: '#2d3748' }}>Student Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p><strong>Name:</strong> {certificate.studentData.name}</p>
                    <p><strong>Student ID:</strong> {certificate.studentData.studentId}</p>
                    <p><strong>Email:</strong> {certificate.studentData.email}</p>
                    {certificate.studentData.phone && (
                      <p><strong>Phone:</strong> {certificate.studentData.phone}</p>
                    )}
                    {certificate.studentData.dateOfBirth && (
                      <p><strong>Date of Birth:</strong> {new Date(certificate.studentData.dateOfBirth).toLocaleDateString()}</p>
                    )}
                  </div>
                  <div>
                    {certificate.studentData.address && (
                      <div>
                        <p><strong>Address:</strong></p>
                        <p style={{ fontSize: '0.9rem', color: '#718096' }}>
                          {certificate.studentData.address.street && `${certificate.studentData.address.street}, `}
                          {certificate.studentData.address.city && `${certificate.studentData.address.city}, `}
                          {certificate.studentData.address.state && `${certificate.studentData.address.state}, `}
                          {certificate.studentData.address.country}
                          {certificate.studentData.address.zipCode && ` - ${certificate.studentData.address.zipCode}`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h4 style={{ fontWeight: '600', marginBottom: '1rem', color: '#2d3748' }}>Academic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p><strong>Degree:</strong> {certificate.academicData.degree}</p>
                    {certificate.academicData.specialization && (
                      <p><strong>Specialization:</strong> {certificate.academicData.specialization}</p>
                    )}
                    <p><strong>Duration:</strong> {certificate.academicData.duration}</p>
                    <p><strong>Academic Period:</strong> {certificate.academicData.admissionYear} - {certificate.academicData.graduationYear}</p>
                  </div>
                  <div>
                    <p><strong>Overall CGPA:</strong> {certificate.academicData.overallCGPA}/10</p>
                    <p><strong>Overall Percentage:</strong> {certificate.academicData.overallPercentage}%</p>
                    <p><strong>Classification:</strong> {certificate.academicData.classification}</p>
                    {certificate.academicData.attendance?.overall && (
                      <p><strong>Attendance:</strong> {certificate.academicData.attendance.overall}%</p>
                    )}
                  </div>
                </div>
              </div>

              {(certificate.academicData.technicalSkills?.length > 0 || certificate.academicData.softSkills?.length > 0) && (
                <div className="mb-6">
                  <h4 style={{ fontWeight: '600', marginBottom: '1rem', color: '#2d3748' }}>Skills</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {certificate.academicData.technicalSkills?.length > 0 && (
                      <div>
                        <p><strong>Technical Skills:</strong></p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                          {certificate.academicData.technicalSkills.map((skill, index) => (
                            <span key={index} className="badge badge-success" style={{ fontSize: '0.8rem' }}>
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {certificate.academicData.softSkills?.length > 0 && (
                      <div>
                        <p><strong>Soft Skills:</strong></p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                          {certificate.academicData.softSkills.map((skill, index) => (
                            <span key={index} className="badge badge-warning" style={{ fontSize: '0.8rem' }}>
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {certificate.academicData.achievements?.length > 0 && (
                <div className="mb-6">
                  <h4 style={{ fontWeight: '600', marginBottom: '1rem', color: '#2d3748' }}>Achievements</h4>
                  <div className="grid grid-cols-1 gap-3">
                    {certificate.academicData.achievements.map((achievement, index) => (
                      <div key={index} style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                          <h5 style={{ fontWeight: '600', margin: 0 }}>{achievement.title}</h5>
                          {achievement.category && (
                            <span className="badge badge-success" style={{ fontSize: '0.7rem' }}>
                              {achievement.category}
                            </span>
                          )}
                        </div>
                        {achievement.description && (
                          <p style={{ fontSize: '0.9rem', color: '#718096', margin: '0.5rem 0' }}>
                            {achievement.description}
                          </p>
                        )}
                        {achievement.date && (
                          <p style={{ fontSize: '0.8rem', color: '#a0aec0', margin: 0 }}>
                            {new Date(achievement.date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {certificate.academicData.thesis?.title && (
                <div className="mb-6">
                  <h4 style={{ fontWeight: '600', marginBottom: '1rem', color: '#2d3748' }}>Thesis/Project</h4>
                  <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                    <p><strong>Title:</strong> {certificate.academicData.thesis.title}</p>
                    {certificate.academicData.thesis.guide && (
                      <p><strong>Guide:</strong> {certificate.academicData.thesis.guide}</p>
                    )}
                    {certificate.academicData.thesis.grade && (
                      <p><strong>Grade:</strong> {certificate.academicData.thesis.grade}</p>
                    )}
                    {certificate.academicData.thesis.abstract && (
                      <div>
                        <p><strong>Abstract:</strong></p>
                        <p style={{ fontSize: '0.9rem', color: '#718096', fontStyle: 'italic' }}>
                          {certificate.academicData.thesis.abstract}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h4 style={{ fontWeight: '600', marginBottom: '1rem', color: '#2d3748' }}>Blockchain Verification</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p><strong>Status:</strong>
                      <span className={`badge badge-${certificate.status === 'MINTED' ? 'success' : 'warning'}`} style={{ marginLeft: '0.5rem' }}>
                        {certificate.status}
                      </span>
                    </p>
                    <p><strong>Certificate Hash:</strong>
                      <code style={{ fontSize: '0.8rem', background: '#f7fafc', padding: '0.2rem 0.4rem', borderRadius: '4px' }}>
                        {certificate.blockchain.certificateHash.substring(0, 20)}...
                      </code>
                    </p>
                    {certificate.tokenId && (
                      <p><strong>Token ID:</strong> {certificate.tokenId}</p>
                    )}
                  </div>
                  <div>
                    <p><strong>Issued:</strong> {new Date(certificate.issuedAt).toLocaleDateString()}</p>
                    <p><strong>Verified:</strong> {certificate.verificationCount} times</p>
                    {certificate.lastVerified && (
                      <p><strong>Last Verified:</strong> {new Date(certificate.lastVerified).toLocaleDateString()}</p>
                    )}
                    <p><strong>API Code:</strong>
                      <code style={{ fontSize: '0.8rem', background: '#f7fafc', padding: '0.2rem 0.4rem', borderRadius: '4px' }}>
                        {certificate.apiCode}
                      </code>
                    </p>
                  </div>
                </div>
              </div>

              {certificate.metadata.qrCode && (
                <div className="text-center">
                  <h4 style={{ fontWeight: '600', marginBottom: '1rem', color: '#2d3748' }}>QR Code for Verification</h4>
                  <img
                    src={certificate.metadata.qrCode}
                    alt="Certificate QR Code"
                    style={{ maxWidth: '200px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  />
                  <p style={{ fontSize: '0.8rem', color: '#718096', marginTop: '0.5rem' }}>
                    Scan this QR code to verify the certificate authenticity
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentAccess;